import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { JuanaCards } from '../../../core/models/juana/juana.types';
import { FleetHealthSummary, FleetVehicleSummary } from '../../../core/models/salud/fleet-health.model';
import { MaintenanceHistoryItem, MaintenanceHistoryResult } from '../../../core/models/salud/maintenance-history.model';

/** Vehículo recién creado por la tool `create_vehicle`. */
export interface CreatedVehicleCard {
  id: string;
  plate: string;
  brand: string;
  model: string;
  year: string;
  color?: string;
  vehicleType?: string;
  status?: string;
}

/** Vehículo elegible desde la tarjeta `fleet-vehicles`. */
export interface JuanaCardVehicle {
  brand?: string;
  model?: string;
  year?: string | number;
  plate?: string;
}

/** Una opción seleccionable de la tarjeta `field-options`. */
export interface JuanaFieldOption {
  value: string;
  label: string;
}

/** Tarjeta `field-options`: Juana pide un valor puntual para un campo (p. ej. tipo de vehículo, estado). */
export interface JuanaFieldOptionsCard {
  field: string;
  label: string;
  options: JuanaFieldOption[];
}

/** Selección emitida por la tarjeta `field-options`. */
export interface JuanaFieldOptionSelection {
  field: string;
  value: string;
  label: string;
}

/** Conductor recién creado por la tool `create_driver`. */
export interface CreatedDriverCard {
  id: string;
  name: string;
  lastname: string;
  email: string;
  phone?: string;
  driverStatus?: string;
}

/** Datos de la oferta sobre la que el usuario pidió comprar (CTA de la tarjeta). */
export interface JuanaBuyRequest {
  responseId: string;
  providerName: string;
  requestId?: string;
  price?: number | null;
  deliveryOptions?: 'pickup' | 'delivery' | 'both' | null;
  deliveryFee?: number | null;
}

/** Una fila de la tarjeta `booking-services` (tool `search_services`). */
export interface JuanaServiceOption {
  providerId: string;
  providerName: string;
  serviceId: string;
  serviceName: string;
  subServiceId: string;
  subServiceName: string;
  price: number | null;
}

/** CTA "Agendar" de una fila de `booking-services`: mismos datos que la fila. */
export type JuanaScheduleRequest = JuanaServiceOption;

/** Confirmación de la cita creada por el mini-form de agendar (fuera del LLM). */
export interface BookingConfirmedCard {
  serviceName: string;
  subServiceName: string;
  providerName: string;
  plate: string;
  date: string;
  hour: string;
  totalAmount: number | null;
}

/**
 * PLAN-Pitts-Juana-General-FRONT.md §4.3 — switch de `kind` que antes vivía
 * inline en agent-panel.component.html. Solo pinta: no conoce el ciclo de
 * chat ni el flujo de compra, que siguen en el wrapper que lo consume
 * (panel de Repuestos hoy, home y widget después).
 */
@Component({
  selector: 'app-juana-card-renderer',
  standalone: false,
  templateUrl: './juana-card-renderer.component.html',
  styleUrl: './juana-card-renderer.component.scss',
})
export class JuanaCardRendererComponent {
  @Input() card: JuanaCards | null = null;
  /** Bloquea los CTA mientras Juana está respondiendo (mismo `busy` del chat). */
  @Input() busy = false;
  /** Oferta con el mini-form de compra abierto; el wrapper es el dueño de ese estado. */
  @Input() openBuyResponseId: string | null = null;
  /**
   * Mini-form de compra, declarado por el wrapper: se renderiza en lugar del
   * CTA de la oferta abierta. Va como TemplateRef para que la lógica de
   * órdenes (y sus estilos) no bajen a este componente.
   */
  @Input() buyFormTpl: TemplateRef<unknown> | null = null;
  /** Fila de `booking-services` con el mini-form de agendar abierto (rowKey). */
  @Input() openScheduleKey: string | null = null;
  /** Mini-form de agendar, declarado por el wrapper (mismo patrón que buyFormTpl). */
  @Input() scheduleFormTpl: TemplateRef<unknown> | null = null;

  @Output() chooseVehicle = new EventEmitter<JuanaCardVehicle>();
  @Output() otherVehicle = new EventEmitter<void>();
  @Output() viewRequest = new EventEmitter<string | undefined>();
  @Output() buy = new EventEmitter<JuanaBuyRequest>();
  @Output() fieldOptionSelected = new EventEmitter<JuanaFieldOptionSelection>();
  @Output() schedule = new EventEmitter<JuanaScheduleRequest>();

  isBuyOpen(responseId: string): boolean {
    return !!responseId && this.openBuyResponseId === responseId;
  }

  /** Clave estable de una fila de `booking-services`: no trae un id propio. */
  rowKey(r: { providerId: string; subServiceId: string }): string {
    return `${r.providerId}:${r.subServiceId}`;
  }

  isScheduleOpen(key: string): boolean {
    return !!key && this.openScheduleKey === key;
  }

  /** Contrato de GET /corporate/fleet/health, reusado tal cual por la tool `get_fleet_health`. */
  get fleetHealth(): FleetHealthSummary | null {
    return this.card?.kind === 'fleet-health' ? (this.card as unknown as FleetHealthSummary) : null;
  }

  get fleetVehicles(): FleetVehicleSummary[] {
    return this.fleetHealth?.vehicles ?? [];
  }

  /** Confirmación de la tool `create_vehicle` (la única que escribe en la flota). */
  get createdVehicle(): CreatedVehicleCard | null {
    return this.card?.kind === 'vehicle-created'
      ? ((this.card as unknown as { vehicle: CreatedVehicleCard }).vehicle ?? null)
      : null;
  }

  /** Opciones de un campo puntual pedidas por Juana (p. ej. tipo de vehículo, estado). */
  get fieldOptions(): JuanaFieldOptionsCard | null {
    return this.card?.kind === 'field-options'
      ? (this.card as unknown as JuanaFieldOptionsCard)
      : null;
  }

  /** Confirmación de la tool `create_driver` (la única que agrega conductores a la flota). */
  get createdDriver(): CreatedDriverCard | null {
    return this.card?.kind === 'driver-created'
      ? ((this.card as unknown as { driver: CreatedDriverCard }).driver ?? null)
      : null;
  }

  /** Contrato de GET /corporate/vehicles/{id}/maintenance-history. */
  get maintenanceHistory(): MaintenanceHistoryResult | null {
    return this.card?.kind === 'maintenance-history'
      ? (this.card as unknown as MaintenanceHistoryResult)
      : null;
  }

  get maintenanceItems(): MaintenanceHistoryItem[] {
    return this.maintenanceHistory?.items ?? [];
  }

  /** Confirmación de la cita creada por el mini-form de agendar (no por una tool). */
  get bookingConfirmed(): BookingConfirmedCard | null {
    return this.card?.kind === 'booking-confirmed'
      ? ((this.card as unknown as { booking: BookingConfirmedCard }).booking ?? null)
      : null;
  }
}
